import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X, Phone, GraduationCap, Sparkles } from "lucide-react";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Home", href: "#home" },
    { name: "Courses", href: "#courses" },
    { name: "Why Us", href: "#why-us" },
    { name: "Placements", href: "#placements" },
    { name: "Testimonials", href: "#testimonials" },
    { name: "Contact", href: "#enquiry" },
  ];

  // Track click conversions using global function
  const trackCallClick = () => {
    if (window.trackClickConversion) {
      window.trackClickConversion('phone_call', 'Navbar - Call');
    }
  };

  const trackRegisterClick = () => {
    if (window.trackClickConversion) {
      window.trackClickConversion('register_click', 'Navbar - Register Now');
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-md shadow-soft border-b border-[hsl(345,20%,90%)]"
          : "bg-white/80 backdrop-blur-sm"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#home" className="flex items-center space-x-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[hsl(345,60%,35%)] to-[hsl(345,55%,45%)] flex items-center justify-center shadow-burgundy">
              <span className="text-white font-display font-bold text-xl">SR</span>
            </div>
            <div className="hidden sm:block">
              <h1 className="font-display text-lg font-semibold text-[hsl(0,0%,15%)]">
                Shri Ram Institute
              </h1>
              <p className="text-xs text-[hsl(345,60%,35%)]">Hotel Management</p>
            </div>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="text-[hsl(0,0%,30%)] hover:text-[hsl(345,60%,35%)] transition-colors duration-300 text-sm font-medium underline-burgundy"
              >
                {link.name}
              </a>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <a 
              href="tel:+917055547000" 
              onClick={trackCallClick}
              className="flex items-center space-x-2 text-[hsl(345,60%,35%)] hover:text-[hsl(345,55%,45%)] transition-colors"
            >
              <Phone className="w-4 h-4" />
              <span className="text-sm font-medium">+91 7055547000</span>
            </a>
            <a 
              href="/apply"
              onClick={trackRegisterClick}
              className="relative group flex items-center gap-2 bg-gradient-to-r from-[hsl(345,60%,35%)] to-[hsl(345,55%,45%)] hover:from-[hsl(345,60%,40%)] hover:to-[hsl(345,55%,50%)] text-white px-5 py-2.5 rounded-full font-semibold text-sm shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden"
              data-testid="navbar-register-btn"
            >
              <GraduationCap className="w-4 h-4" />
              <span>Register Now 2026</span>
              {/* Shine effect */}
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            </a>
          </div>

          {/* Mobile: Register Now Button + Menu */}
          <div className="flex lg:hidden items-center gap-2">
            {/* Mobile Register Now CTA */}
            <a 
              href="/apply"
              onClick={trackRegisterClick}
              className="relative flex items-center gap-1.5 bg-gradient-to-r from-[hsl(345,60%,35%)] to-[hsl(345,55%,45%)] text-white px-3 py-2 rounded-full font-bold text-xs shadow-md overflow-hidden"
              data-testid="navbar-register-btn-mobile"
            >
              <Sparkles className="w-3 h-3" />
              <span>Register 2026</span>
            </a>
            
            {/* Mobile Menu Button */}
            <button
              className="p-2 text-[hsl(0,0%,30%)] hover:text-[hsl(345,60%,35%)] transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-[hsl(345,20%,90%)] animate-slide-down">
            <div className="py-4 space-y-2">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="block px-4 py-3 text-[hsl(0,0%,30%)] hover:text-[hsl(345,60%,35%)] hover:bg-[hsl(345,60%,35%)/0.05] transition-all duration-300 text-sm font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.name}
                </a>
              ))}
              <div className="px-4 pt-4 border-t border-[hsl(345,20%,90%)]">
                <a 
                  href="/apply"
                  onClick={() => { trackRegisterClick(); setIsMobileMenuOpen(false); }}
                  className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-[hsl(345,60%,35%)] to-[hsl(345,55%,45%)] text-white px-5 py-3 rounded-lg font-bold text-sm shadow-md"
                >
                  <GraduationCap className="w-5 h-5" />
                  <span>Register Now 2026</span>
                  <span className="bg-[hsl(43,74%,50%)] text-[10px] px-2 py-0.5 rounded-full">Limited Seats</span>
                </a>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navbar;
